import type { SupabaseClient } from "@supabase/supabase-js";
import type { OutboxOp } from "../db/outbox.ts";
import { Superseded, type PullResult, type Transport } from "./transport.ts";

/**
 * Every insert below is written so that replaying it is a no-op rather than
 * a duplicate: append-only rows collide on their unique `client_id`, and
 * readings on (measurement_id, position_id). `ignoreDuplicates` turns that
 * collision into silence instead of an error, which is what lets the outbox
 * replay blindly after a sync that died halfway.
 */
export class SupabaseTransport implements Transport {
  private sb: SupabaseClient;
  constructor(sb: SupabaseClient) { this.sb = sb; }

  async apply(op: OutboxOp): Promise<void> {
    const sb = this.sb;
    switch (op.kind) {
      /* A sauna created on the device before anyone signed in. It goes up as
         one unit — a stove with no sauna, or points with no stove, would be
         worse than nothing. The server's trigger makes the pusher its owner,
         which is what then lets the positions through. */
      case "insert_sauna": {
        /* The sauna is a plain insert, not an upsert. `on conflict` makes
           Postgres evaluate the UPDATE policy as well, and that policy
           requires ownership of a sauna that does not exist yet — so the
           upsert is refused with "new row violates row-level security
           policy for table sauna" while a plain insert is allowed. A
           replayed insert collides on the primary key instead, which is
           the same idempotency by a different route.
           The stove and the points stay upserts: by then the bootstrap
           trigger has made this member the owner, so their UPDATE policies
           pass. */
        await this.insertIgnoringDuplicate("sauna", op.row);
        this.check(await sb.from("stove").upsert(op.stove, { onConflict: "id", ignoreDuplicates: true }));
        return this.check(await sb.from("position")
          .upsert(op.positions, { onConflict: "id", ignoreDuplicates: true }));
      }

      /* The layout and its starting targets belong to the owner, and only one
         person edits them, so the whole row is replaced rather than merged.
         Retiring a point is the same write with retired_at set — a point is
         never deleted, because the readings taken at it still refer to it. */
      // The sauna's own details. An owner-only update, and only ever fields
      // the owner typed — a location is never derived from anything.
      case "update_sauna":
        return this.check(await sb.from("sauna").update(op.patch).eq("id", op.id));

      case "upsert_position":
        return this.check(await sb.from("position").upsert(op.row, { onConflict: "id" }));

      case "insert_session":
        return this.check(await sb.from("heat_session")
          .upsert(op.row, { onConflict: "client_id", ignoreDuplicates: true }));

      /* The function answers false when the patch lost the last-write-wins
         comparison, and check() looks only at `error` — so a losing patch
         used to count as delivered and vanish from the queue without a word.
         It is delivered, but the user has to hear about it. */
      case "patch_session": {
        const { data, error } = await sb.rpc("session_field_patch", {
          p_session: op.session_id, p_field: op.field,
          p_value: op.value, p_changed_at: op.changed_at,
        });
        if (error) throw new Error(error.message);
        if (data === false) throw new Superseded(op.field);
        return;
      }

      case "insert_measurement": {
        this.check(await sb.from("measurement")
          .upsert(op.row, { onConflict: "client_id", ignoreDuplicates: true }));
        return this.check(await sb.from("reading")
          .upsert(op.readings, { onConflict: "measurement_id,position_id", ignoreDuplicates: true }));
      }

      // A correction replaces the values it corrects, so this one upserts
      // for real rather than ignoring the collision.
      case "update_readings": {
        this.check(await sb.from("measurement").update({ edited: true }).eq("id", op.measurement_id));
        return this.check(await sb.from("reading")
          .upsert(op.readings, { onConflict: "measurement_id,position_id" }));
      }

      case "insert_group":
        return this.check(await sb.from("bathing_group")
          .upsert(op.row, { onConflict: "client_id", ignoreDuplicates: true }));

      case "patch_group": {
        const patch: Record<string, unknown> = { ended_at: op.ended_at };
        if (op.person_count !== undefined) patch["person_count"] = op.person_count;
        return this.check(await sb.from("bathing_group").update(patch).eq("id", op.id));
      }

      case "insert_fuel":
        return this.check(await sb.from("fuel_load")
          .upsert(op.row, { onConflict: "client_id", ignoreDuplicates: true }));

      // A rating is one per member per session by construction, so replacing
      // it is correct — unlike the append-only rows, a second rating from the
      // same person is a correction, not a new fact.
      case "upsert_rating":
        return this.check(await sb.from("rating")
          .upsert(op.row, { onConflict: "session_id,user_id" }));

      case "insert_comment":
        return this.check(await sb.from("comment")
          .upsert(op.row, { onConflict: "client_id", ignoreDuplicates: true }));

      case "insert_stove_event":
        return this.check(await sb.from("stove_event").upsert(op.row));
    }
  }

  async mySaunas(): Promise<Record<string, unknown>[]> {
    return this.rowsOf("sauna", (q) => q);   // RLS narrows it to this member's
  }

  async pull(sauna_id: string, _since: string | null): Promise<PullResult> {
    /* Everything for this sauna, every time.
     *
     * There used to be an `updated_at >= cursor` filter, and the cursor was
     * stamped with the CLIENT's clock — so a row written on the server while
     * a device was mid-pull, or any skew between two phones, dropped rows
     * permanently: the cursor moved past them and nothing ever asked again.
     * The symptom is the worst kind, a second device that simply never sees
     * an evening.
     *
     * A sauna is heated about a dozen times a year. The whole archive is a
     * few hundred rows, and fetching it is cheaper than being wrong.
     */
    const rows: PullResult["rows"] = {};
    rows["sauna"] = await this.rowsOf("sauna", (q) => q.eq("id", sauna_id));
    rows["stove"] = await this.rowsOf("stove", (q) => q.eq("sauna_id", sauna_id));
    rows["membership"] = await this.rowsOf("membership", (q) => q.eq("sauna_id", sauna_id));

    const sessions = await this.rowsOf("heat_session", (q) => q.eq("sauna_id", sauna_id));
    rows["heat_session"] = sessions;

    const ids = sessions.map((s) => s["id"] as string);
    if (ids.length) {
      const ms = await this.rowsOf("measurement", (q) => q.in("session_id", ids));
      rows["measurement"] = ms;
      const mids = ms.map((m) => m["id"] as string);
      if (mids.length) {
        rows["reading"] = await this.rowsOf("reading", (q) => q.in("measurement_id", mids));
      }
      rows["bathing_group"] = await this.rowsOf("bathing_group", (q) => q.in("session_id", ids));
      rows["fuel_load"] = await this.rowsOf("fuel_load", (q) => q.in("session_id", ids));
      rows["rating"] = await this.rowsOf("rating", (q) => q.in("session_id", ids));
      rows["comment"] = await this.rowsOf("comment", (q) => q.in("session_id", ids));
    }

    const stoveIds = (rows["stove"] ?? []).map((s) => s["id"] as string);
    if (stoveIds.length) {
      rows["position"] = await this.rowsOf("position", (q) => q.in("stove_id", stoveIds));
      rows["stove_event"] = await this.rowsOf("stove_event", (q) => q.in("stove_id", stoveIds));
    }
    return { server_time: new Date().toISOString(), rows };
  }

  private async rowsOf(
    table: string,
    shape: (q: any) => any,
  ): Promise<Record<string, unknown>[]> {
    const { data, error } = await shape(this.sb.from(table).select("*"));
    if (error) throw new Error(`${table}: ${error.message}`);
    return (data ?? []) as Record<string, unknown>[];
  }

  private check(res: { error: { message: string } | null }): void {
    if (res.error) throw new Error(res.error.message);
  }

  /** A row that is already there was already accepted. That is success. */
  private async insertIgnoringDuplicate(
    table: string, row: Record<string, unknown>,
  ): Promise<void> {
    const { error } = await this.sb.from(table).insert(row);
    if (!error) return;
    const dup = (error as { code?: string }).code === "23505" ||
      /duplicate key|already exists/i.test(error.message);
    if (!dup) throw new Error(error.message);
  }
}
